import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Bell, CheckCheck } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { buildAuctionSlug } from "@/lib/auction-slug";
import { useTranslation, translate } from "@/i18n";
import type { Locale } from "@/i18n/config";

type NotificationRow = {
  id: string;
  type: string;
  title: string | null;
  message: string | null;
  created_at: string;
  read_at: string | null;
  auction_id: string | null;
  metadata: Record<string, unknown> | null;
};

function timeAgo(iso: string, locale: Locale): string {
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return translate(locale, "activity", "bell.timeJustNow", "just now");
  const m = Math.floor(s / 60);
  if (m < 60) return translate(locale, "activity", "bell.timeMinutes", "{count}m ago", { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return translate(locale, "activity", "bell.timeHours", "{count}h ago", { count: h });
  const d = Math.floor(h / 24);
  if (d < 7) return translate(locale, "activity", "bell.timeDays", "{count}d ago", { count: d });
  const w = Math.floor(d / 7);
  if (w < 5) return translate(locale, "activity", "bell.timeWeeks", "{count}w ago", { count: w });
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

function resolveHref(n: NotificationRow): string {
  const meta = n.metadata ?? {};
  const url = typeof meta.url === "string" ? meta.url : null;
  if (url) return url;
  if (n.auction_id) return `/auction/${buildAuctionSlug({ id: n.auction_id })}`;
  return "/notifications";
}

export function NotificationBell() {
  const { t, locale } = useTranslation("activity");
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [, forceTick] = useState(0);

  // Re-render every 30s so relative timestamps stay fresh.
  useEffect(() => {
    const id = window.setInterval(() => forceTick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const { data } = useQuery({
    queryKey: ["notifications-inbox", user?.id],
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("id,type,title,message,created_at,read_at,auction_id,metadata")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false })
        .limit(15);
      if (error) throw error;
      return (data ?? []) as NotificationRow[];
    },
  });

  // Realtime: refresh inbox when a new row lands.
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: ["notifications-inbox", user.id] });
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user, qc]);

  const unread = useMemo(
    () => (data ?? []).filter((n) => !n.read_at).length,
    [data],
  );

  const markOne = async (id: string) => {
    const nowIso = new Date().toISOString();
    qc.setQueryData<NotificationRow[]>(
      ["notifications-inbox", user?.id],
      (prev) =>
        (prev ?? []).map((n) => (n.id === id ? { ...n, read_at: nowIso } : n)),
    );
    const { error } = await supabase
      .from("notifications")
      .update({ read_at: nowIso })
      .eq("id", id);
    if (error) {
      toast.error(t("bell.toastMarkOneError", "Could not mark as read"));
      qc.invalidateQueries({ queryKey: ["notifications-inbox", user?.id] });
    }
  };

  const markAll = async () => {
    if (!user) return;
    const ids = (data ?? []).filter((n) => !n.read_at).map((n) => n.id);
    if (ids.length === 0) return;
    const nowIso = new Date().toISOString();
    qc.setQueryData<NotificationRow[]>(
      ["notifications-inbox", user.id],
      (prev) =>
        (prev ?? []).map((n) =>
          n.read_at ? n : { ...n, read_at: nowIso },
        ),
    );
    const { error } = await supabase
      .from("notifications")
      .update({ read_at: nowIso })
      .eq("user_id", user.id)
      .is("read_at", null);
    if (error) {
      toast.error(t("bell.toastMarkAllError", "Could not mark all as read"));
      qc.invalidateQueries({ queryKey: ["notifications-inbox", user.id] });
    } else {
      toast.success(t("bell.toastMarkAllSuccess", "All caught up"));
    }
  };

  if (!user) return null;

  const items = data ?? [];
  const badge = unread > 9 ? "9+" : String(unread);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={
            unread > 0
              ? t("bell.ariaUnread", "Notifications, {count} unread", { count: unread })
              : t("bell.ariaNoUnread", "Notifications")
          }
          className="relative h-10 w-10 rounded-xl border border-white/10 bg-white/5 backdrop-blur-xl hover:border-white/20 hover:bg-white/10"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              aria-hidden="true"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-gold-gradient text-[10px] font-semibold text-background flex items-center justify-center shadow-[0_2px_8px_rgba(212,175,55,0.5)]"
            >
              {badge}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[360px] p-0 rounded-2xl border-white/10 bg-background/95 backdrop-blur-xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div>
            <p className="text-sm font-semibold">{t("bell.title", "Notifications")}</p>
            <p className="text-xs text-muted-foreground">
              {unread > 0 ? t("bell.unreadCount", "{count} unread", { count: unread }) : t("bell.allCaughtUp", "You're all caught up")}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={markAll}
            disabled={unread === 0}
            className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <CheckCheck className="h-3.5 w-3.5 mr-1" />
            {t("bell.markAllRead", "Mark all read")}
          </Button>
        </div>

        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <Bell className="h-8 w-8 mx-auto text-muted-foreground/40" />
              <p className="mt-3 text-sm text-muted-foreground">
                {t("bell.empty", "No notifications yet.")}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {items.map((n) => {
                const href = resolveHref(n);
                const unreadDot = !n.read_at;
                return (
                  <li key={n.id}>
                    <Link
                      to={href}
                      onClick={() => {
                        if (unreadDot) void markOne(n.id);
                        setOpen(false);
                      }}
                      className={`flex gap-3 px-4 py-3 hover:bg-white/[0.04] transition-colors ${
                        unreadDot ? "bg-white/[0.02]" : ""
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                          unreadDot
                            ? "bg-gold-gradient shadow-[0_0_8px_rgba(212,175,55,0.6)]"
                            : "bg-transparent"
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <p
                          className={`text-sm truncate ${
                            unreadDot
                              ? "text-foreground font-medium"
                              : "text-muted-foreground"
                          }`}
                        >
                          {n.title ?? t("bell.defaultTitle", "Notification")}
                        </p>
                        {n.message && (
                          <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                            {n.message}
                          </p>
                        )}
                        <p className="mt-1 text-[11px] text-muted-foreground/70">
                          {timeAgo(n.created_at, locale)}
                        </p>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="border-t border-white/10 px-4 py-2.5">
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="block text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("bell.settingsLink", "Notification settings")}
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
